import React, { useState, useEffect } from "react";
import { useDispatch } from "react-redux";
import Image from "next/image";
import { setPayloadData } from "@store/slices/globalState";
import {
  Container,
  Tile,
  SelectorContainer,
  Selector,
  SelectorText
  // StyledArrow
} from "./styles";

import { sampleAnalytics } from "@utils/index";
import { resolvePath } from "@utils/resolvePath";

export const currentYear = new Date().getFullYear();
export const minimumRegistrationYear = 2001;
export const beforeMinimumRegistrationYear = `Before ${minimumRegistrationYear}`;
export const registrationMaskedValue = minimumRegistrationYear - 1;

const monthOptionsDefault = [
  {
    name: "Jan",
    displayName: "January",
    value: 1
  },
  {
    name: "Feb",
    displayName: "February",
    value: 2
  },
  {
    name: "Mar",
    displayName: "March",
    value: 3
  },
  {
    name: "Apr",
    displayName: "April",
    value: 4
  },
  {
    name: "May",
    displayName: "May",
    value: 5
  },
  {
    name: "Jun",
    displayName: "June",
    value: 6
  },
  {
    name: "Jul",
    displayName: "July",
    value: 7
  },
  {
    name: "Aug",
    displayName: "August",
    value: 8
  },
  {
    name: "Sep",
    displayName: "September",
    value: 9
  },
  {
    name: "Oct",
    displayName: "October",
    value: 10
  },
  {
    name: "Nov",
    displayName: "November",
    value: 11
  },
  {
    name: "Dec",
    displayName: "December",
    value: 12
  }
];

type YearOption = {
  name: number | string;
  value: number | string;
  className: string;
  is_registration_year_masked?: boolean;
  maskedValue?: number;
};

const numericYearOptions: YearOption[] = Array.from(
  { length: currentYear - minimumRegistrationYear + 1 },
  (_, index) => {
    const year = currentYear - index;
    return {
      name: year,
      value: year,
      className: "registrationYear"
    };
  }
);

const yearOptionsDefault: YearOption[] = [
  ...numericYearOptions,
  {
    name: beforeMinimumRegistrationYear,
    value: "before-minimum-registration-year",
    is_registration_year_masked: true,
    maskedValue: registrationMaskedValue,
    className: "registrationYear"
  }
];

const YearMonthSelector = ({
  showModal,
  setShowModal,
  monthOptions = monthOptionsDefault,
  activeMonth,
  setActiveMonth,
  activeYear,
  setActiveYear,
  yearOptions = yearOptionsDefault,
  callbackFunction
}: any) => {
  const dispatch = useDispatch();
  const [selector, setSelector] = useState("year");
  const [selectedMonth, setSelectedMonth] = useState(activeMonth);
  const [selectedYear, setSelectedYear] = useState(activeYear);
  const currentYear = new Date().getFullYear();
  const currentMonth = new Date().getMonth();

  if (selectedYear === currentYear) {
    monthOptions = monthOptions.filter(
      (item: any) => (item.value as number) - 1 <= currentMonth
    );
  }

  useEffect(() => {
    showModal && sampleAnalytics(
      "view",
      "view_modal",
      selector === "year"
        ? "view_modal_select_registration_year"
        : "view_modal_select_registration_month"
    );
  }, [showModal, selector]),
    
  useEffect(() => {
    if (
      selectedYear === currentYear &&
      monthOptions.findIndex((month: any) => month.name === selectedMonth) ===
        -1
    ) {
      setActiveMonth("Jan");
      setSelectedMonth("Jan");
    }
  }, [showModal, selectedYear]);

  useEffect(() => {
    showModal && setSelector("year");
  }, [showModal]);

  const displayMonth = monthOptions.filter(
    (month: any) => month.name === selectedMonth
  )[0]?.displayName;

  const months = monthOptions.map((month: any) => {
    return (
      <Tile
        key={month.value}
        selected={month.name === selectedMonth}
        onClick={() => {
          setSelectedMonth(month.name);
          setActiveMonth(month.name);
          if (selectedYear !== "") {
            setShowModal(false);
            callbackFunction(selectedYear, month.name);
          }
           sampleAnalytics(
             "action",
             "tap_btn",
             "tap_btn_registration_month_selected"
           );
        }}
      >
        {month.name}
      </Tile>
    );
  });
  console.log("regyear showmodal", showModal);
  const years = yearOptions.map((year: any) => {
    return (
      <Tile
        key={year.value}
        selected={year.name === selectedYear}
        onClick={() => {
          setActiveYear(year.name);
          setSelectedYear(year.name);
          if (year.is_registration_year_masked) {
            dispatch(
              setPayloadData({
                is_registration_year_masked: true
              })
            );
            setActiveMonth("Jan");
            setShowModal(false);
            callbackFunction(year.maskedValue, "Jan");
          } else {
            dispatch(
              setPayloadData({
                is_registration_year_masked: false
              })
            );
            setSelector("month");
          }
          sampleAnalytics(
            "action",
            "tap_btn",
            "tap_btn_registration_year_selected",
            { registration_year: year.is_registration_year_masked ? year.maskedValue : year.name }
          );
        }}
      >
        {year.name}
      </Tile>
    );
  });

  return (
    <>
      {showModal && (
        <>
          <SelectorContainer>
            <Selector
              onClick={() => {
                setSelector("year");
              }}
              selected={selector === "year"}
            >
              <SelectorText selected={selector === "year"}>
                {selectedYear || "Year"}
              </SelectorText>
              <Image
                src={resolvePath("/images/icons/chevron-down.svg")}
                width={20}
                height={20}
                style={{
                  transform: `${selector === "year" ? "rotate(180deg)" : "rotate(0deg)"}`,
                  transitionDuration: "100ms"
                }}
              />
            </Selector>
            <Selector
              onClick={() => {
                setSelector("month");
              }}
              selected={selector === "month"}
            >
              <SelectorText selected={selector === "month"}>
                {displayMonth || "Month"}
              </SelectorText>
              <Image
                src={resolvePath("/images/icons/chevron-down.svg")}
                width={20}
                height={20}
                style={{
                  transform: `${selector === "month" ? "rotate(180deg)" : "rotate(0deg)"}`,
                  transitionDuration: "100ms"
                }}
              />
            </Selector>
          </SelectorContainer>

          <Container>
            {selector === "month" && months}

            {selector === "year" && years}
          </Container>
        </>
      )}
    </>
  );
};

export default YearMonthSelector;
